/** Display logic for the tracker: formatting, wind interpretation, route
 * progress, and time scrubbing.
 *
 * Everything here is pure, so the map component stays a thin shell and the logic
 * that could be wrong in a way viewers would notice is unit tested.
 *
 * Sign conventions come from the API and are NOT re-derived here:
 * `headwind_kmh` is positive against the rider and negative for a tailwind, and
 * `direction_deg` is the direction the wind blows FROM.
 */
import bbox from "@turf/bbox";
import { lineString } from "@turf/helpers";
import turfLength from "@turf/length";
import lineSliceAlong from "@turf/line-slice-along";
import type { Feature, FeatureCollection, LineString, Position } from "geojson";

import type { Arrival, RiderProgress, Route, Stage, WindReading } from "./api";

/** Fallback trace colours for riders with no colour configured.
 *
 * Riders are identified on the map by line colour, so a collision is a real
 * legibility bug: these stay distinguishable against a muted basemap and remain
 * separable under the common forms of colour vision deficiency.
 */
export const RIDER_PALETTE = [
  "#1b6ac9",
  "#c2410c",
  "#15803d",
  "#7c3aed",
  "#b91c1c",
  "#0f766e",
] as const;

export function riderColor(rider: { color: string }, index: number): string {
  const configured = rider.color?.trim();
  if (configured) return configured;
  return RIDER_PALETTE[index % RIDER_PALETTE.length] as string;
}

/** Extract a ride slug from a `/r/<slug>` path, or null for any other route.
 *
 * The tracker is its own entry point rather than a screen inside an admin app:
 * viewers have a link and no account. Pure, so the routing decision is testable
 * without mounting anything. The character class also keeps `..` and extra path
 * segments out of a URL we are about to fetch.
 */
export function publicRideSlug(pathname: string): string | null {
  // `/race/<slug>` is canonical; `/r/<slug>` still works because links to it are
  // already in people's messages and a share link that stops resolving is worse
  // than a second spelling of the same route.
  const match = /^\/(?:race|r)\/([A-Za-z0-9_-]{1,120})\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

/** The canonical path for a race, used by the switcher and the share link. */
export function racePath(slug: string): string {
  return `/race/${encodeURIComponent(slug)}`;
}

export function isAdminPath(pathname: string): boolean {
  return /^\/admin\/?$/.test(pathname);
}

/** Order stages by their own names, with numeric collation.
 *
 * The name comes from inside each GPX file, NOT from the filename: a browser
 * download is often prefixed with a hash ("5415dfb3-TAWI_F8.gpx"), so sorting
 * filenames sorts the hashes and scrambles the stages.
 *
 * Numeric collation is what makes real stage names work: it puts F9 before F10
 * (a plain string sort would not), and F3 before F3.1 before F4.
 */
export function orderStagesByName<T extends { name: string }>(stages: T[]): T[] {
  return [...stages].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }),
  );
}

/** The instant the wind layer should show.
 *
 * Rider playback has a time only when fixes exist; the wind does not depend on
 * that. A ride whose route is loaded but whose riders have not reported yet —
 * the state every ride starts in, and the state the bundled route ships in — had
 * no time at all, so the wind layer silently never rendered. It falls back to the
 * current hour instead.
 *
 * Rounded down to the hour deliberately: the wind API is cached per (ride, hour),
 * so an unrounded "now" would miss that cache on every render.
 */
export function windTimeMs(viewAtMs: number | null, nowMs: number): number {
  if (viewAtMs !== null) return viewAtMs;
  const hour = 60 * 60 * 1000;
  return Math.floor(nowMs / hour) * hour;
}

// ------------------------------------------------------------------ formatting

export function formatDistance(metres: number | null): string {
  if (metres === null || !Number.isFinite(metres)) return "—";
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(metres < 10_000 ? 1 : 0)} km`;
}

export function formatSpeed(kmh: number | null): string {
  return kmh === null || !Number.isFinite(kmh) ? "—" : `${kmh.toFixed(1)} km/h`;
}

/** "12 min ago" / "3 h ago".
 *
 * This reports staleness rather than implying a stationary rider: a Garmin gap
 * usually means no sky view, not a stop.
 */
export function formatLastSeen(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "no fix yet";
  const seen = new Date(iso).getTime();
  if (!Number.isFinite(seen)) return "no fix yet";
  const minutes = Math.max(0, Math.round((now.getTime() - seen) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** The newest fix on the ride, however it arrived, or null if there are none.
 *
 * "Synced" is one fact for the whole race, so it does not distinguish a Garmin
 * poll from a hand-placed point: both answer "how stale is this page". A rider
 * with no fix yet contributes nothing rather than counting as zero.
 */
export function lastSynced(
  riders: readonly { last_seen_at: string | null }[],
  arrivals: readonly { arrived_at: string | null }[] = [],
  stages: readonly { started_at: string | null }[] = [],
): string | null {
  let newest: string | null = null;
  let newestMs = -Infinity;
  const consider = (iso: string | null) => {
    if (!iso) return;
    const ms = new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms <= newestMs) return;
    newestMs = ms;
    newest = iso;
  };
  for (const rider of riders) consider(rider.last_seen_at);
  // A race with no trackers on it is still being kept up to date — by hand,
  // through arrivals and stage starts. Reading only device fixes meant the line
  // was blank on exactly the deployment that most needed it.
  for (const arrival of arrivals) consider(arrival.arrived_at);
  for (const stage of stages) consider(stage.started_at);
  return newest;
}

/** A timestamp short enough for a subtitle, long enough to be unambiguous.
 *
 * The shape follows what the reader is actually asking. Within the hour the
 * question is "is this current?", which a relative time answers best. Later the
 * same day it is "when?", and a clock time is shorter and more precise than
 * counting hours back. Further out the date has to appear, and the year only
 * once it is not this one — the usual shorthand rule, and what keeps the common
 * case brief.
 *
 * 24-hour, because the timeline clock beside it is, and two time formats on one
 * screen is a reading cost for no gain.
 */
export function formatSynced(
  iso: string | null,
  now: Date = new Date(),
  timeZone?: string,
): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return null;

  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  // A clock skewed a little fast should not report the future.
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;

  const zone = timeZone ? { timeZone } : {};
  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("en-GB", { ...zone, ...options }).format(at);
  const clock = part({ hour: "2-digit", minute: "2-digit", hour12: false });

  const dayKey = (date: Date) =>
    new Intl.DateTimeFormat("en-CA", { ...zone, dateStyle: "short" }).format(date);
  const today = dayKey(now);
  if (dayKey(at) === today) return clock;

  const yesterday = new Date(now.getTime() - 86_400_000);
  if (dayKey(at) === dayKey(yesterday)) return `${clock} yesterday`;

  // Sliced to three letters: en-GB renders September as "Sept" in newer ICU
  // builds and "Sep" in older ones, which would make the line's width depend on
  // the browser and a test depend on the runner.
  const month = part({ month: "short" }).slice(0, 3);
  const stamp = `${clock}, ${part({ day: "numeric" })} ${month}`;
  const thisYear = new Intl.DateTimeFormat("en-GB", { ...zone, year: "numeric" }).format(
    now,
  );
  const year = part({ year: "numeric" });
  return year === thisYear ? stamp : `${stamp} ${year}`;
}

/** "2026-09-18 07:00 GMT+2" — the viewer's own clock, with the zone named.
 *
 * This used to print UTC, on the grounds that UTC is what the feed reports. But
 * every time an organiser types into this app they type local time: the start
 * and arrival fields are `datetime-local`, which the browser fills from the
 * machine's clock. Reading 05:00Z back out of a box you typed 07:00 into is a
 * data-entry trap, and the people watching a race are in its timezone.
 *
 * The zone is always shown rather than left implied, so the one viewer abroad
 * is not quietly misled — and as a GMT offset rather than an abbreviation,
 * since which abbreviations exist depends on the browser's ICU build.
 *
 * `timeZone` is for tests, which must not depend on the machine's own.
 */
export function formatClock(at: Date, timeZone?: string): string {
  if (!Number.isFinite(at.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("en-GB", {
    ...(timeZone ? { timeZone } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "shortOffset",
  }).formatToParts(at);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  // Assembled from parts rather than taking the locale's own order, so the date
  // stays ISO-shaped and sorts the way the rest of this app's dates do.
  return (
    `${get("year")}-${get("month")}-${get("day")} ` +
    `${get("hour")}:${get("minute")} ${get("timeZoneName")}`
  );
}

// -------------------------------------------------------------------- the wind

export type WindRelation = "headwind" | "tailwind" | "crosswind";

/** Classify a reading for a rider travelling the route here.
 *
 * The threshold is on the head/tail component rather than the angle, so a light
 * breeze from straight ahead is not dressed up as a headwind worth naming.
 */
export function windRelation(reading: WindReading, thresholdKmh = 5): WindRelation {
  if (reading.headwind_kmh >= thresholdKmh) return "headwind";
  if (reading.headwind_kmh <= -thresholdKmh) return "tailwind";
  return "crosswind";
}

export function describeWind(reading: WindReading): string {
  const relation = windRelation(reading);
  const speed = Math.round(reading.speed_kmh);
  if (relation === "crosswind") return `${speed} km/h crosswind`;
  return `${speed} km/h wind — ${Math.abs(Math.round(reading.headwind_kmh))} km/h ${relation}`;
}

/** Arrow rotation in MapLibre's clockwise-from-north degrees.
 *
 * The glyph points the way the air is GOING, which is 180° from the
 * meteorological "blowing from" direction the API reports.
 */
export function windArrowRotation(reading: WindReading): number {
  return (reading.direction_deg + 180) % 360;
}

/** A one-line summary of what the wind is doing to the riders right now. */
export function summariseWind(readings: WindReading[]): string {
  if (readings.length === 0) return "no wind data";
  const head = readings.filter((r) => windRelation(r) === "headwind").length;
  const tail = readings.filter((r) => windRelation(r) === "tailwind").length;
  const strongest = readings.reduce((worst, r) =>
    Math.abs(r.headwind_kmh) > Math.abs(worst.headwind_kmh) ? r : worst,
  );
  const parts = [`${readings.length} samples`];
  if (head) parts.push(`${head} headwind`);
  if (tail) parts.push(`${tail} tailwind`);
  // "strongest", not "worst": the reading picked is the largest headwind
  // COMPONENT, which on a good day is a tailwind — and a 16 km/h push is not
  // the worst of anything.
  parts.push(`strongest ${describeWind(strongest)}`);
  return parts.join(" · ");
}

/** The same field in a phrase short enough for a collapsed one-line bar.
 *
 * The long summary is four facts joined by separators, which wrapped to two
 * lines on a phone — so the bar that exists to save a quarter of the screen
 * spent a line of it saying so. Collapsed, the useful fact is what the wind is
 * doing to you and how hard; the counts are there when it is opened.
 */
export function summariseWindShort(readings: WindReading[]): string {
  if (readings.length === 0) return "Wind";
  const strongest = readings.reduce((worst, r) =>
    Math.abs(r.headwind_kmh) > Math.abs(worst.headwind_kmh) ? r : worst,
  );
  const relation = windRelation(strongest);
  const speed =
    relation === "crosswind"
      ? Math.round(strongest.speed_kmh)
      : Math.abs(Math.round(strongest.headwind_kmh));
  return `Wind · ${speed} km/h ${relation}`;
}

// ------------------------------------------------------------ route geometry

/** Split one stage into ridden and remaining parts at `distanceM` INTO that stage.
 *
 * This is what shows progress honestly: the completed stretch is drawn solid and
 * the rest muted, instead of leaving viewers to infer it from a breadcrumb trail
 * whose 10-minute spacing cuts across every bend.
 *
 * Returns `ridden: null` at the start line and `remaining: null` at the finish,
 * because a zero-length slice is not a drawable line.
 */
export function splitRouteAtDistance(
  coordinates: [number, number][],
  distanceM: number | null,
): { ridden: Feature<LineString> | null; remaining: Feature<LineString> | null } {
  if (coordinates.length < 2) return { ridden: null, remaining: null };
  const route = lineString(coordinates as Position[]);
  const totalKm = turfLength(route, { units: "kilometers" });
  const atKm = Math.min(Math.max((distanceM ?? 0) / 1000, 0), totalKm);

  // turf works in kilometres; guard both ends so a rider exactly at the start or
  // the finish cannot produce a degenerate slice.
  return {
    ridden: atKm > 0.001 ? lineSliceAlong(route, 0, atKm) : null,
    remaining: totalKm - atKm > 0.001 ? lineSliceAlong(route, atKm, totalKm) : null,
  };
}

/** Split a whole multi-stage route at a cumulative distance.
 *
 * Each stage is handled on its own — fully ridden, fully ahead, or split part-way
 * — rather than slicing one concatenated line. That is what keeps a transfer
 * between stages from being drawn, and therefore from being shown as ridden.
 */
export function splitStagesAtDistance(
  stages: Stage[],
  distanceM: number | null,
): { ridden: Feature<LineString>[]; remaining: Feature<LineString>[] } {
  const ridden: Feature<LineString>[] = [];
  const remaining: Feature<LineString>[] = [];
  const reached = distanceM ?? 0;

  for (const stage of stages) {
    const into = reached - stage.start_distance_m;
    const properties = { seq: stage.seq, name: stage.name };
    const parts = splitRouteAtDistance(stage.coordinates, into);
    if (parts.ridden) ridden.push({ ...parts.ridden, properties });
    if (parts.remaining) remaining.push({ ...parts.remaining, properties });
  }
  return { ridden, remaining };
}

/** Every stage as one full LineString, the base the route is always drawn as.
 *
 * The grey route line is NOT the "not yet ridden" remainder any more. It is the
 * whole planned route, drawn underneath, with rider progress and confirmed
 * arrivals painted over it. Splitting the base line meant the route visibly
 * shortened as riders advanced, which is the opposite of what a route line is
 * for — and it left nothing grey to read the course from.
 */
export function stageLines(stages: Stage[]): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: stages
      .filter((stage) => stage.coordinates.length >= 2)
      .map((stage) => ({
        type: "Feature" as const,
        geometry: { type: "LineString" as const, coordinates: stage.coordinates },
        properties: { seq: stage.seq, name: stage.name },
      })),
  };
}

/** Points for each recorded arrival, labelled with the town where there is one. */
export function arrivalMarkers(arrivals: Arrival[], stages: Stage[]): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: arrivals.flatMap((arrival) => {
      const point = pointAtDistance(stages, arrival.distance_m);
      if (!point) return [];
      return [
        {
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: point },
          properties: {
            id: arrival.id,
            label: arrival.town_name ?? arrival.stage_name ?? "arrived",
          },
        },
      ];
    }),
  };
}

/** The coordinate a cumulative route distance falls on, walking the stages.
 *
 * Returns null past the end of the route rather than clamping to the finish: an
 * arrival recorded before the stages were re-uploaded shorter should disappear
 * from the map rather than sit misleadingly on the last point. A distance inside
 * a transfer gap also returns null — there is no route there to put it on.
 */
export function pointAtDistance(
  stages: Stage[],
  distanceM: number,
): [number, number] | null {
  for (const stage of stages) {
    if (stage.coordinates.length < 2) continue;
    const into = distanceM - stage.start_distance_m;
    if (into < 0) return null;
    if (into > stage.length_m) continue;

    const line = lineString(stage.coordinates as Position[]);
    const totalKm = turfLength(line, { units: "kilometers" });
    const atKm = Math.min(Math.max(into / 1000, 0), totalKm);
    if (atKm <= 0) return [...(stage.coordinates[0] as [number, number])];
    const sliced = lineSliceAlong(line, 0, atKm);
    const coords = sliced.geometry.coordinates;
    const last = coords[coords.length - 1];
    if (last) return [last[0] as number, last[1] as number];
  }
  return null;
}

/** A point at each stage's start, for labelling the stages on the map. */
export function stageMarkers(stages: Stage[]): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: stages.flatMap((stage) => {
      const start = stage.coordinates[0];
      if (!start) return [];
      return [
        {
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: start as Position },
          properties: {
            seq: stage.seq,
            name: stage.name,
            label: `${stage.seq + 1}. ${stage.name}`,
          },
        },
      ];
    }),
  };
}

/** How far through a stage the confirmed progress is, as a percentage — or null
 * when there is no partial progress to report.
 *
 * Null in both the uninteresting cases, which is what makes it a chip worth
 * reading: a stage not started yet says nothing, and a finished one already has
 * a "complete" badge. Only a stage actually underway gets a number.
 *
 * Rounded away from 0 and 100 rather than to them. A rider 40 m into a 112 km
 * stage is not "0% done" and a rider 60 m from its end is not "100% done" — in
 * both cases the number would contradict the badge sitting next to it.
 */
export function stagePercentComplete(
  stage: { start_distance_m: number; length_m: number },
  completedDistanceM: number | null,
): number | null {
  if (completedDistanceM === null || !Number.isFinite(completedDistanceM)) return null;
  if (stage.length_m <= 0) return null;
  const into = completedDistanceM - stage.start_distance_m;
  if (into <= 0 || into >= stage.length_m) return null;
  const percent = (into / stage.length_m) * 100;
  return Math.min(99, Math.max(1, Math.round(percent)));
}

/** The two ends worth flagging: where you set off, and where you finish.
 *
 * With no stage selected that is the whole race — the start of the first stage
 * and the end of the last. With one selected it is that stage's own ends, since
 * a selected stage is the thing being read and its finish is the one that
 * matters. Either way there are exactly two, so they never accumulate into the
 * row of intermediate dots the per-stage markers already provide.
 *
 * Stages are taken in `seq` order rather than array order: "first" and "last"
 * are facts about the route, not about how the payload happened to be sorted.
 */
export function endpointMarkers(
  stages: Stage[],
  focusSeq: number | null,
): FeatureCollection {
  const ordered = [...stages].sort((a, b) => a.seq - b.seq);
  const focused =
    focusSeq === null ? null : ordered.find((stage) => stage.seq === focusSeq);
  if (focusSeq !== null && !focused) return { type: "FeatureCollection", features: [] };

  const from = focused ?? ordered[0];
  const to = focused ?? ordered[ordered.length - 1];
  if (!from || !to) return { type: "FeatureCollection", features: [] };

  const start = from.coordinates[0];
  const finish = to.coordinates[to.coordinates.length - 1];
  if (!start || !finish) return { type: "FeatureCollection", features: [] };

  const point = (kind: "start" | "finish", at: number[], label: string) => ({
    type: "Feature" as const,
    geometry: { type: "Point" as const, coordinates: at as Position },
    properties: { kind, label },
  });

  return {
    type: "FeatureCollection",
    features: [
      point("start", start, focused ? `Start · ${focused.name}` : "Start"),
      point("finish", finish, focused ? `Finish · ${focused.name}` : "Finish"),
    ],
  };
}

/** Bounds covering every stage and every rider position, or null if neither. */
export function trackBounds(
  route: Route | null,
  progress: RiderProgress[],
): [number, number, number, number] | null {
  const positions: Position[] = [];
  if (route) {
    for (const stage of route.stages)
      positions.push(...(stage.coordinates as Position[]));
  }
  for (const rider of progress) {
    if (rider.longitude !== null && rider.latitude !== null) {
      positions.push([rider.longitude, rider.latitude]);
    }
  }
  if (positions.length === 0) return null;
  if (positions.length === 1) {
    // A single point has no extent; pad it so fitBounds doesn't zoom to infinity.
    const [lon, lat] = positions[0] as [number, number];
    return [lon - 0.05, lat - 0.05, lon + 0.05, lat + 0.05];
  }
  const box = bbox(lineString(positions));
  return [box[0], box[1], box[2], box[3]];
}

// ------------------------------------------------------------------ elevation

export type ElevationProfile = {
  /** `[metres along the stage, metres above sea level]`, in order. */
  points: [number, number][];
  minM: number;
  maxM: number;
  /** Total metres climbed: the sum of the ups only, which is the number a
   * cyclist means by "how much climbing". Descent is reported separately. */
  ascentM: number;
  descentM: number;
};

/** Ignore wobbles below this when totting up climbing.
 *
 * Consumer GPS elevation is noisy by a metre or two per point, and summing every
 * positive difference over ~1,500 points turns that noise into hundreds of
 * metres of phantom climbing. Two metres is the usual threshold for this. */
const ASCENT_NOISE_M = 2;

/** An elevation profile for one stage, or null when it has no elevation data. */
export function elevationProfile(stage: Stage): ElevationProfile | null {
  const elevations = stage.elevations;
  // A misaligned profile is dropped rather than plotted against the wrong
  // places; the API drops it too, so this is belt and braces.
  if (!elevations || elevations.length !== stage.coordinates.length) return null;
  if (elevations.length < 2) return null;

  const points: [number, number][] = [];
  let along = 0;
  for (let i = 0; i < elevations.length; i += 1) {
    if (i > 0) {
      const from = stage.coordinates[i - 1] as [number, number];
      const to = stage.coordinates[i] as [number, number];
      along +=
        turfLength(lineString([from, to] as Position[]), { units: "kilometers" }) * 1000;
    }
    points.push([along, elevations[i] as number]);
  }

  const { ascentM, descentM } = climbing(elevations);

  return {
    points,
    minM: Math.min(...elevations),
    maxM: Math.max(...elevations),
    ascentM: Math.round(ascentM),
    descentM: Math.round(descentM),
  };
}

/** Total ascent and descent, with GPS noise filtered by hysteresis.
 *
 * Peak-and-trough rather than summing differences. Two simpler approaches both
 * fail, and failed here before this:
 *
 * * Summing every positive step counts receiver noise as climbing — ±1 m over
 *   1,500 points invents hundreds of metres.
 * * Ignoring steps below a threshold, one neighbour at a time, throws away a
 *   real climb that happens to be sampled in 1 m increments.
 *
 * So a direction is only reversed once the elevation has turned back by more
 * than the threshold, and the whole swing from the last reversal is committed
 * at that point. Noise never reverses direction; a long gradual climb is
 * counted in full.
 */
function climbing(elevations: number[]): { ascentM: number; descentM: number } {
  let ascentM = 0;
  let descentM = 0;
  let anchor = elevations[0] as number; // last confirmed reversal
  let extreme = anchor; // furthest point reached since the anchor
  let direction = 0; // 0 undecided, 1 rising, -1 falling

  for (const value of elevations) {
    if (direction === 0) {
      if (value - anchor > ASCENT_NOISE_M) {
        direction = 1;
        extreme = value;
      } else if (anchor - value > ASCENT_NOISE_M) {
        direction = -1;
        extreme = value;
      }
      continue;
    }
    if (direction === 1) {
      if (value > extreme) {
        extreme = value;
      } else if (extreme - value > ASCENT_NOISE_M) {
        ascentM += extreme - anchor;
        anchor = extreme;
        extreme = value;
        direction = -1;
      }
    } else if (value < extreme) {
      extreme = value;
    } else if (value - extreme > ASCENT_NOISE_M) {
      descentM += anchor - extreme;
      anchor = extreme;
      extreme = value;
      direction = 1;
    }
  }

  // The final leg never reverses, so it has to be committed explicitly.
  if (direction === 1) ascentM += extreme - anchor;
  if (direction === -1) descentM += anchor - extreme;
  return { ascentM, descentM };
}

/** An SVG path for a profile, scaled into a `width` x `height` box.
 *
 * Returned as a string so the chart is plain SVG with no charting dependency —
 * one polyline is not worth 40 kB of library.
 */
export function elevationPath(
  profile: ElevationProfile,
  width: number,
  height: number,
): string {
  const [first] = profile.points;
  const last = profile.points[profile.points.length - 1];
  if (!first || !last) return "";
  const spanX = last[0] - first[0] || 1;
  // A flat stage would divide by zero; give it a nominal span so it draws as a
  // line through the middle rather than vanishing.
  const spanY = profile.maxM - profile.minM || 1;
  return profile.points
    .map(([along, metres], index) => {
      const x = ((along - first[0]) / spanX) * width;
      const y = height - ((metres - profile.minM) / spanY) * height;
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/** Where along an elevation chart the confirmed progress has reached, in the
 * chart's own x units — or null when there is nothing partial to draw.
 *
 * Shares `elevationPath`'s mapping rather than assuming one: the profile is
 * plotted against distance along the stage, not against sample index, so a
 * route decimated unevenly would put the marker in the wrong place if this
 * divided by the number of points.
 *
 * Null follows the same rule as the percentage chip — a stage not started and a
 * stage finished both say so already, and a marker pinned to either end of the
 * chart is a line that means nothing.
 */
/** How much of a stage is ridden, 0 to 1, for shading its profile.
 *
 * Separate from the marker below because they answer different questions. The
 * marker is a position and only exists while there is one — pinned to either
 * end of a chart it means nothing. The shading is an amount, and "all of it" is
 * a perfectly good amount: a stage shown green on the map should not have a
 * profile that is entirely pink, which is one rule looking like two.
 */
export function elevationDoneFraction(
  stage: { length_m: number },
  intoStageM: number | null,
): number {
  if (intoStageM === null || !Number.isFinite(intoStageM)) return 0;
  if (stage.length_m <= 0) return 0;
  return Math.max(0, Math.min(1, intoStageM / stage.length_m));
}

export function elevationProgressX(
  profile: ElevationProfile,
  width: number,
  intoStageM: number | null,
): number | null {
  if (intoStageM === null || !Number.isFinite(intoStageM)) return null;
  const [first] = profile.points;
  const last = profile.points[profile.points.length - 1];
  if (!first || !last) return null;
  const spanX = last[0] - first[0];
  if (spanX <= 0) return null;
  const x = ((intoStageM - first[0]) / spanX) * width;

  // Tested in chart units, not in metres. A finished stage arrives here as its
  // own length, but that length and the profile's span are computed from
  // different things and disagree in the last decimal — so a metres comparison
  // let a complete stage through at x = 999.9999999, drawing a marker hard
  // against the right edge of a chart that is entirely done.
  const edge = 0.5;
  if (!Number.isFinite(x) || x <= edge || x >= width - edge) return null;
  return x;
}

/** What the profile reads at a fraction of the way across it: the distance into
 * the stage, the elevation there, and the coordinate to put a dot on the map.
 *
 * Interpolated between the two neighbouring points using the profile's own
 * distances, so the dot on the map and the bar on the chart are measured by the
 * same ruler. `coordinates` is the stage's, index-aligned with the profile —
 * which elevationProfile guarantees by refusing a misaligned one.
 */
export function profileSampleAt(
  profile: ElevationProfile,
  coordinates: [number, number][],
  fraction: number,
): { alongM: number; elevationM: number; coordinate: [number, number] } | null {
  const { points } = profile;
  const [first] = points;
  const last = points[points.length - 1];
  if (!first || !last || coordinates.length !== points.length) return null;

  const alongM = first[0] + Math.min(Math.max(fraction, 0), 1) * (last[0] - first[0]);
  // Binary search for the first point at or beyond alongM: profiles run to
  // thousands of points and this runs on every pointer move.
  let lo = 1;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((points[mid] as [number, number])[0] < alongM) lo = mid + 1;
    else hi = mid;
  }
  const [a0, e0] = points[lo - 1] as [number, number];
  const [a1, e1] = points[lo] as [number, number];
  const [x0, y0] = coordinates[lo - 1] as [number, number];
  const [x1, y1] = coordinates[lo] as [number, number];
  // Repeated points give a zero-length segment; take its start.
  const t = a1 > a0 ? (alongM - a0) / (a1 - a0) : 0;
  return {
    alongM,
    elevationM: e0 + (e1 - e0) * t,
    coordinate: [x0 + (x1 - x0) * t, y0 + (y1 - y0) * t],
  };
}

/** Bounding box of one stage, for zooming to it. */
export function stageBounds(stage: Stage): [number, number, number, number] | null {
  if (stage.coordinates.length < 2) return null;
  const box = bbox(lineString(stage.coordinates as Position[]));
  return [box[0], box[1], box[2], box[3]];
}

// ----------------------------------------------------------------- time scrub

/** How far back a timeline reaches when there is no ride history to span. */
const EMPTY_TIMELINE_HOURS = 12;

/** The timeline's span: earliest ping to **now**.
 *
 * It used to end at the latest ping, which meant the clock read whenever the
 * last satellite pass happened to be — commonly a quarter of an hour ago, and
 * hours ago for a device in a pocket. The right-hand end of a live tracker is
 * the present, and the wind layer reads its hour from this, so stopping short
 * also pinned the weather to the past.
 *
 * With no pings at all there is still a timeline, covering the last
 * {@link EMPTY_TIMELINE_HOURS} hours, so the wind can be scrubbed on a route
 * whose riders have not reported yet.
 */
export function timelineRange(
  tracks: FeatureCollection,
  nowMs: number,
): { from: Date; to: Date } {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const feature of tracks.features) {
    if (feature.properties?.kind !== "ping") continue;
    const at = new Date(String(feature.properties.recorded_at)).getTime();
    if (!Number.isFinite(at)) continue;
    min = Math.min(min, at);
    max = Math.max(max, at);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return {
      from: new Date(nowMs - EMPTY_TIMELINE_HOURS * 60 * 60 * 1000),
      to: new Date(nowMs),
    };
  }
  // A ping timestamped slightly ahead of this clock (device time, or a stale
  // `nowMs` between refreshes) must not produce a backwards range.
  return { from: new Date(min), to: new Date(Math.max(max, nowMs)) };
}

/** Pings at or before `at` — the scrubber's filter.
 *
 * Track LineStrings are dropped rather than clipped: the scrubbed view is built
 * from points, and a full-history line behind a partial set of points would
 * quietly contradict it.
 */
export function pingsUpTo(tracks: FeatureCollection, at: Date): FeatureCollection {
  const cutoff = at.getTime();
  return {
    type: "FeatureCollection",
    features: tracks.features.filter((feature) => {
      if (feature.properties?.kind !== "ping") return false;
      const seen = new Date(String(feature.properties.recorded_at)).getTime();
      return Number.isFinite(seen) && seen <= cutoff;
    }),
  };
}

/** Per-rider traces rebuilt from the pings visible at a given moment. */
export function tracesUpTo(
  tracks: FeatureCollection,
  at: Date,
): FeatureCollection<LineString> {
  const visible = pingsUpTo(tracks, at);
  const byRider = new Map<string, { color: string; name: string; points: Position[] }>();

  for (const feature of visible.features) {
    const props = feature.properties ?? {};
    const riderId = String(props.rider_id);
    const entry = byRider.get(riderId) ?? {
      color: String(props.color ?? ""),
      name: String(props.display_name ?? ""),
      points: [],
    };
    if (feature.geometry.type === "Point") {
      entry.points.push(feature.geometry.coordinates as Position);
    }
    byRider.set(riderId, entry);
  }

  const features: Feature<LineString>[] = [];
  for (const [riderId, entry] of byRider) {
    // One point is a position, not a trace; MapLibre would reject the geometry.
    if (entry.points.length < 2) continue;
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: entry.points },
      properties: {
        kind: "track",
        rider_id: riderId,
        display_name: entry.name,
        color: entry.color,
      },
    });
  }
  return { type: "FeatureCollection", features };
}

/** Positions of each rider at the scrubbed moment: their last ping at or before it. */
export function positionsAt(tracks: FeatureCollection, at: Date): FeatureCollection {
  const latest = new Map<string, { feature: Feature; at: number }>();
  const cutoff = at.getTime();

  for (const feature of tracks.features) {
    if (feature.properties?.kind !== "ping") continue;
    const seen = new Date(String(feature.properties.recorded_at)).getTime();
    if (!Number.isFinite(seen) || seen > cutoff) continue;
    const riderId = String(feature.properties.rider_id);
    const current = latest.get(riderId);
    if (!current || seen > current.at) latest.set(riderId, { feature, at: seen });
  }

  return {
    type: "FeatureCollection",
    features: [...latest.values()].map(({ feature }) => feature),
  };
}
